import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { PFISymbol, BrandGradient } from '../components/common/PFILogo';
import { formatDate } from '../utils/dateFormatters';
import { 
  CheckCircleIcon, 
  DocumentTextIcon, 
  ClockIcon,
  ExclamationCircleIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

export default function JobSheetAcknowledgmentPage() {
  const { jobSheetId } = useParams();
  const [jobSheet, setJobSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);
  const [newQuery, setNewQuery] = useState({
    queryText: '',
    priority: 'medium',
    category: 'clarification'
  });

  useEffect(() => {
    loadJobSheet();
  }, [jobSheetId]);

  const loadJobSheet = async () => {
    try {
      setLoading(true);
      const jobSheetDoc = await getDoc(doc(db, 'jobSheets', jobSheetId));
      
      if (jobSheetDoc.exists()) {
        const data = jobSheetDoc.data();
        setJobSheet({ 
          id: jobSheetDoc.id, 
          ...data,
          sentAt: data.sentAt.toDate(),
          acknowledgedAt: data.acknowledgedAt?.toDate(),
          acknowledgmentDeadline: data.acknowledgmentDeadline.toDate(),
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate()
        });
        setAcknowledged(!!data.acknowledgedAt);
      }
    } catch (error) {
      console.error('Error loading job sheet:', error);
      alert('Failed to load job sheet: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async () => {
    if (!jobSheet) return;
    
    setAcknowledging(true);
    try {
      const now = new Date();
      
      // Update job sheet
      await updateDoc(doc(db, 'jobSheets', jobSheetId), {
        acknowledgedAt: now,
        status: 'acknowledged',
        updatedAt: now
      });

      // Update task status
      await updateDoc(doc(db, 'projects', jobSheet.projectId, 'tasks', jobSheet.taskId), {
        status: 'job_sheet_acknowledged',
        jobSheetAcknowledgedAt: now,
        updatedAt: now
      });

      // Create notification for PFI
      await addDoc(collection(db, 'notifications'), {
        type: 'job-sheet-acknowledged',
        recipientId: 'admin', // Or specific admin user ID
        recipientEmail: jobSheet.contactInfo.email,
        projectId: jobSheet.projectId,
        projectName: jobSheet.projectDetails.name,
        taskId: jobSheet.taskId,
        taskName: jobSheet.taskDetails.name,
        supplierName: jobSheet.supplierName,
        referenceNumber: jobSheet.referenceNumber,
        message: `Job sheet acknowledged by ${jobSheet.supplierName}`,
        acknowledgedAt: now,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      setAcknowledged(true);
      alert('✅ Job sheet acknowledged successfully!\n\nYou can now submit queries if needed.');
      
    } catch (error) {
      console.error('Error acknowledging job sheet:', error);
      alert('Failed to acknowledge job sheet: ' + error.message);
    } finally {
      setAcknowledging(false);
    }
  };

  const handleSubmitQuery = async (e) => {
    e.preventDefault();
    if (!jobSheet || !newQuery.queryText.trim()) return;

    try {
      const now = new Date();
      
      // Create query
      await addDoc(collection(db, 'supplierQueries'), {
        jobSheetId: jobSheet.id,
        taskId: jobSheet.taskId,
        projectId: jobSheet.projectId,
        supplierId: jobSheet.supplierId,
        supplierName: jobSheet.supplierName,
        queryText: newQuery.queryText,
        status: 'pending',
        priority: newQuery.priority,
        category: newQuery.category,
        createdAt: now,
        updatedAt: now
      });

      // Update task status to indicate queries pending
      await updateDoc(doc(db, 'projects', jobSheet.projectId, 'tasks', jobSheet.taskId), {
        status: 'queries_pending',
        updatedAt: now
      });

      // Create notification for PFI
      await addDoc(collection(db, 'notifications'), {
        type: 'supplier-query-submitted',
        recipientId: 'admin',
        recipientEmail: jobSheet.contactInfo.email,
        projectId: jobSheet.projectId,
        projectName: jobSheet.projectDetails.name,
        taskId: jobSheet.taskId,
        taskName: jobSheet.taskDetails.name,
        supplierName: jobSheet.supplierName,
        referenceNumber: jobSheet.referenceNumber,
        queryText: newQuery.queryText,
        queryPriority: newQuery.priority,
        queryCategory: newQuery.category,
        message: `New query from ${jobSheet.supplierName}: ${newQuery.queryText.substring(0, 100)}...`,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      alert('✅ Query submitted successfully!\n\nPink Flamingo Interiors has been notified and will respond shortly.');
      
      setNewQuery({ queryText: '', priority: 'medium', category: 'clarification' });
      setShowQueryForm(false);
      
    } catch (error) {
      console.error('Error submitting query:', error);
      alert('Failed to submit query: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading job sheet...</p>
        </div>
      </div>
    );
  }

  if (!jobSheet) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Job Sheet Not Found</h1>
          <p className="text-gray-600">The job sheet you're looking for doesn't exist or may have been removed.</p>
        </div>
      </div>
    );
  }

  const isOverdue = !acknowledged && new Date() > jobSheet.acknowledgmentDeadline;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <BrandGradient className="rounded-none p-8 text-white shadow-xl">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <DocumentTextIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
              <div className="flex-1">
                <h1 className="text-4xl font-bold mb-3">Job Sheet</h1>
                <p className="text-pink-100 text-xl">Reference: {jobSheet.referenceNumber}</p>
              </div>
            </div>
            <Link
              to={`/supplier-dashboard/${jobSheet.referenceNumber}`}
              className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-white rounded-lg hover:bg-opacity-30 transition-all duration-300"
            >
              <ClipboardDocumentCheckIcon className="w-4 h-4 mr-2" />
              My Dashboard
            </Link>
          </div>
          <div className="flex items-start">
            <div className="w-12 mr-6"></div> {/* Spacer to align with content above */}
            <div className="flex-1">
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <ClockIcon className="w-5 h-5 mr-2" />
                  Sent: {formatDate(jobSheet.sentAt)}
                </div>
                {acknowledged ? (
                  <div className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    Acknowledged: {formatDate(jobSheet.acknowledgedAt)}
                  </div>
                ) : (
                  <div className={`flex items-center ${isOverdue ? 'text-red-200' : 'text-yellow-200'}`}>
                    <ExclamationCircleIcon className="w-5 h-5 mr-2" />
                    Due: {formatDate(jobSheet.acknowledgmentDeadline)}
                    {isOverdue && ' (OVERDUE)'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </BrandGradient>

      <div className="max-w-4xl mx-auto p-8">
        {/* Status Alert */}
        {!acknowledged && (
          <div className={`rounded-lg p-4 mb-8 ${
            isOverdue 
              ? 'bg-red-50 border border-red-200' 
              : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center">
              <ExclamationCircleIcon className={`w-5 h-5 mr-3 ${
                isOverdue ? 'text-red-600' : 'text-yellow-600'
              }`} />
              <p className={`font-medium ${
                isOverdue ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {isOverdue 
                  ? 'This job sheet acknowledgment is overdue. Please acknowledge receipt immediately.'
                  : 'Please acknowledge receipt of this job sheet within 48 hours.'
                }
              </p>
            </div>
          </div>
        )}

        <div className="space-y-8">
          {/* Task Details */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Task Details</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Task Name</label>
                <p className="mt-1 text-gray-900 font-medium">{jobSheet.taskDetails.name}</p>
              </div>
              
              {jobSheet.taskDetails.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <p className="mt-1 text-gray-600 whitespace-pre-wrap">{jobSheet.taskDetails.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <p className="mt-1 text-gray-600 capitalize">{jobSheet.taskDetails.category}</p>
                </div>
                
                {jobSheet.taskDetails.budget && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Budget</label>
                    <p className="mt-1 text-gray-600">${jobSheet.taskDetails.budget.toLocaleString()}</p>
                  </div>
                )}
              </div>
              
              {jobSheet.taskDetails.timeline && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Timeline</label>
                  <p className="mt-1 text-gray-600">{jobSheet.taskDetails.timeline}</p>
                </div>
              )}
              
              {jobSheet.taskDetails.specifications && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Specifications</label>
                  <p className="mt-1 text-gray-600 whitespace-pre-wrap">{jobSheet.taskDetails.specifications}</p>
                </div>
              )}

              {jobSheet.taskDetails.notes && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Additional Notes</label>
                  <p className="mt-1 text-gray-600 whitespace-pre-wrap">{jobSheet.taskDetails.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Project Information & Photos */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Project Information</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Project Details */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Project Name</label>
                  <p className="mt-1 text-gray-900 font-medium">{jobSheet.projectDetails.name}</p>
                </div>
                
                {jobSheet.projectDetails.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Project Description</label>
                    <p className="mt-1 text-gray-600 whitespace-pre-wrap">{jobSheet.projectDetails.description}</p>
                  </div>
                )}
                
                {jobSheet.projectDetails.address && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <p className="mt-1 text-gray-600">{jobSheet.projectDetails.address}</p>
                  </div>
                )}
                
                {jobSheet.projectDetails.customerInfo && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Customer</label>
                    <p className="mt-1 text-gray-600">{jobSheet.projectDetails.customerInfo}</p>
                  </div>
                )}

                {jobSheet.projectDetails.startDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Project Start Date</label>
                    <p className="mt-1 text-gray-600">
                      {formatDate(jobSheet.projectDetails.startDate)}
                    </p>
                  </div>
                )}

                {jobSheet.projectDetails.endDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Project End Date</label>
                    <p className="mt-1 text-gray-600">
                      {formatDate(jobSheet.projectDetails.endDate)}
                    </p>
                  </div>
                )}
              </div>

              {/* Project Photos */}
              {jobSheet.projectDetails.photos && jobSheet.projectDetails.photos.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Project Photos</label>
                  <div className="grid grid-cols-2 gap-3">
                    {jobSheet.projectDetails.photos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo.url || photo.downloadURL}
                          alt={photo.name || `Project photo ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-pink-300 transition-colors cursor-pointer"
                          onClick={() => window.open(photo.url || photo.downloadURL, '_blank')}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-opacity flex items-center justify-center">
                          <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                            Click to view
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Click any photo to view full size</p>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* PFI Contact */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Pink Flamingo Interiors</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Primary Contact</label>
                    <p className="mt-1 text-gray-900 font-medium">{jobSheet.contactInfo.primaryContact}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <p className="mt-1 text-gray-600">
                      <a href={`tel:${jobSheet.contactInfo.phone}`} className="hover:text-pink-600 transition-colors">
                        {jobSheet.contactInfo.phone}
                      </a>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-gray-600">
                      <a href={`mailto:${jobSheet.contactInfo.email}`} className="hover:text-pink-600 transition-colors">
                        {jobSheet.contactInfo.email}
                      </a>
                    </p>
                  </div>

                  {jobSheet.contactInfo.address && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="mt-1 text-gray-600">{jobSheet.contactInfo.address}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Contact (if different) */}
              {jobSheet.projectDetails.customerContact && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Contact</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name</label>
                      <p className="mt-1 text-gray-900 font-medium">{jobSheet.projectDetails.customerContact.name}</p>
                    </div>
                    
                    {jobSheet.projectDetails.customerContact.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <p className="mt-1 text-gray-600">
                          <a href={`tel:${jobSheet.projectDetails.customerContact.phone}`} className="hover:text-pink-600 transition-colors">
                            {jobSheet.projectDetails.customerContact.phone}
                          </a>
                        </p>
                      </div>
                    )}
                    
                    {jobSheet.projectDetails.customerContact.email && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-gray-600">
                          <a href={`mailto:${jobSheet.projectDetails.customerContact.email}`} className="hover:text-pink-600 transition-colors">
                            {jobSheet.projectDetails.customerContact.email}
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 space-y-4">
          {!acknowledged && (
            <button
              onClick={handleAcknowledge}
              disabled={acknowledging}
              className="w-full flex items-center justify-center px-8 py-4 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors disabled:opacity-50 text-lg"
            >
              {acknowledging ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Acknowledging...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-6 h-6 mr-3" />
                  Acknowledge Receipt
                </>
              )}
            </button>
          )}
          
          {acknowledged && (
            <button
              onClick={() => setShowQueryForm(!showQueryForm)}
              className="w-full flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-lg"
            >
              <ChatBubbleLeftRightIcon className="w-6 h-6 mr-3" />
              {showQueryForm ? 'Cancel Query' : 'Submit Query'}
            </button>
          )}
        </div>

        {/* Query Form */}
        {showQueryForm && (
          <div className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Submit Query</h3>
            
            <form onSubmit={handleSubmitQuery} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Query Details *</label>
                <textarea
                  value={newQuery.queryText}
                  onChange={(e) => setNewQuery({ ...newQuery, queryText: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Describe your question, clarification needed, or concern about this job..."
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={newQuery.priority}
                    onChange={(e) => setNewQuery({ ...newQuery, priority: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={newQuery.category}
                    onChange={(e) => setNewQuery({ ...newQuery, category: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    <option value="clarification">Clarification</option>
                    <option value="timeline">Timeline</option>
                    <option value="materials">Materials</option>
                    <option value="access">Site Access</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                  Submit Query
                </button>
                <button
                  type="button"
                  onClick={() => setShowQueryForm(false)}
                  className="px-6 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {acknowledged && (
          <div className="mt-8 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-green-600 mr-3" />
              <p className="text-green-800 font-medium">
                Job sheet acknowledged successfully. You can now proceed with the work or submit queries if needed.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}